import React, { useState, useEffect, useRef } from 'react';
import { Award, BookOpen, Upload, Bell, X, Check, AlertCircle } from 'lucide-react';
import useCertificationManagementStore from '@/stores/AdminStore/CertificationManagementStore';
import { uploadCertificationTemplate } from '@/services/UploadService';
import './CreateCertification.css';

interface FormData {
  certification_name: string;
  description: string;
  validity_type: 'lifetime' | 'limited';
  validity_period: number | undefined;
  template_file_path: string;
  status: 'active' | 'inactive';
  course_ids: number[];
}

interface FormErrors {
  certification_name?: string;
  validity_period?: string;
  course_ids?: string;
  template_file_path?: string;
}

const CreateCertification: React.FC = () => {
  const {
    activeCourses,
    certifications,
    loading,
    error,
    fetchActiveCourses,
    fetchCertifications,
    createCertification,
    clearError,
  } = useCertificationManagementStore();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [templateFileName, setTemplateFileName] = useState<string>('');
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [templateError, setTemplateError] = useState<string>('');

  const [formData, setFormData] = useState<FormData>({
    certification_name: '',
    description: '',
    validity_type: 'lifetime',
    validity_period: undefined,
    template_file_path: '',
    status: 'active',
    course_ids: [],
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Fetch active courses + existing certifications on mount so we can check
  // names against the full list for duplicates.
  useEffect(() => {
    if (activeCourses.length === 0) {
      fetchActiveCourses();
    }
    if ((certifications ?? []).length === 0) {
      fetchCertifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear error when component unmounts
  useEffect(() => {
    return () => {
      clearError();
      setSuccessMessage('');
    };
  }, [clearError]);

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    const trimmedName = formData.certification_name.trim();
    if (!trimmedName) {
      errors.certification_name = 'Certification name is required';
    } else {
      const duplicate = (certifications ?? []).some(
        (c) => (c.certification_name ?? '').trim().toLowerCase() === trimmedName.toLowerCase()
      );
      if (duplicate) {
        errors.certification_name = 'A certification with this name already exists';
      }
    }

    if (formData.validity_type === 'limited' && !formData.validity_period) {
      errors.validity_period = 'Validity period is required for limited validity';
    }

    if (formData.course_ids.length === 0) {
      errors.course_ids = 'Please select at least one course';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleTemplateFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setTemplateError('');
    setUploadingTemplate(true);
    try {
      const res = await uploadCertificationTemplate(file);
      if (res.success && res.data?.filename) {
        setFormData((prev) => ({ ...prev, template_file_path: res.data.filename }));
        setTemplateFileName(file.name);
      } else {
        setTemplateError(res.error?.message || 'Upload failed');
      }
    } catch (err: any) {
      setTemplateError(err?.message || 'Upload failed');
    } finally {
      setUploadingTemplate(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleClearTemplate = () => {
    setFormData((prev) => ({ ...prev, template_file_path: '' }));
    setTemplateFileName('');
    setTemplateError('');
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (formErrors[name as keyof FormErrors]) {
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleValidityTypeChange = (type: 'lifetime' | 'limited') => {
    setFormData((prev) => ({
      ...prev,
      validity_type: type,
      validity_period: type === 'lifetime' ? undefined : prev.validity_period,
    }));
    if (type === 'lifetime') {
      setFormErrors((prev) => ({ ...prev, validity_period: undefined }));
    }
  };

  const handleCourseToggle = (courseId: number) => {
    setFormData((prev) => {
      const isSelected = prev.course_ids.includes(courseId);
      return {
        ...prev,
        course_ids: isSelected
          ? prev.course_ids.filter((id) => id !== courseId)
          : [...prev.course_ids, courseId],
      };
    });
    // Clear course selection error
    if (formErrors.course_ids) {
      setFormErrors((prev) => ({ ...prev, course_ids: undefined }));
    }
  };

  const handleSubmit = async (status: 'active' | 'inactive') => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    clearError();
    setSuccessMessage('');

    try {
      const payload = {
        ...formData,
        status,
        validity_period:
          formData.validity_type === 'limited' ? formData.validity_period ?? undefined : undefined,
      };

      const result = await createCertification(payload);

      if (result.success) {
        setSuccessMessage(
          `Certification created successfully! ${status === 'active' ? 'It is now active.' : 'Saved as draft.'}`
        );
        // Reset form
        setFormData({
          certification_name: '',
          description: '',
          validity_type: 'lifetime',
          validity_period: undefined,
          template_file_path: '',
          status: 'active',
          course_ids: [],
        });
        setFormErrors({});
        setTemplateFileName('');
        setTemplateError('');
      }
    } catch (err) {
      console.error('Failed to create certification:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCourseDetails = activeCourses.filter((course) =>
    formData.course_ids.includes(course.id)
  );

  return (
    <div className="admin-certifi-create-certification">
      {/* Success Message */}
      {successMessage && (
        <div className="admin-certifi-alert admin-certifi-alert-success">
          <Check size={20} />
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage('')}>
            <X size={16} />
          </button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="admin-certifi-alert admin-certifi-alert-error">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={clearError}>
            <X size={16} />
          </button>
        </div>
      )}

      <div className="admin-certifi-create-layout">
        <div className="admin-certifi-create-main">
          {/* Certification Information */}
          <div className="admin-certifi-form-section">
            <div className="admin-certifi-section-header">
              <Award size={20} />
              <h3>Certification Information</h3>
            </div>

            <div className="admin-certifi-form-field">
              <label htmlFor="certification_name">
                Certification Name <span className="admin-certifi-required">*</span>
              </label>
              <input
                type="text"
                id="certification_name"
                name="certification_name"
                value={formData.certification_name}
                onChange={handleInputChange}
                placeholder="e.g., Full Stack Developer Certification"
                className={formErrors.certification_name ? 'error' : ''}
              />
              {formErrors.certification_name && (
                <span className="admin-certifi-error-text">{formErrors.certification_name}</span>
              )}
            </div>

            <div className="admin-certifi-form-field">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                placeholder="Describe the certification and its value..."
              />
            </div>

            <div className="admin-certifi-form-row">
              <div className="admin-certifi-form-field">
                <label>
                  Validity Type <span className="admin-certifi-required">*</span>
                </label>
                <div className="admin-certifi-radio-group">
                  <label className="admin-certifi-radio-item">
                    <input
                      type="radio"
                      name="validity_type"
                      checked={formData.validity_type === 'lifetime'}
                      onChange={() => handleValidityTypeChange('lifetime')}
                    />
                    <span>Lifetime</span>
                  </label>
                  <label className="admin-certifi-radio-item">
                    <input
                      type="radio"
                      name="validity_type"
                      checked={formData.validity_type === 'limited'}
                      onChange={() => handleValidityTypeChange('limited')}
                    />
                    <span>Limited Period</span>
                  </label>
                </div>
              </div>

              {formData.validity_type === 'limited' && (
                <div className="admin-certifi-form-field">
                  <label htmlFor="validity_period">
                    Validity Period (Days) <span className="admin-certifi-required">*</span>
                  </label>
                  <input
                    type="number"
                    id="validity_period"
                    name="validity_period"
                    value={formData.validity_period || ''}
                    onChange={handleInputChange}
                    placeholder="e.g., 365"
                    min="1"
                    className={formErrors.validity_period ? 'error' : ''}
                  />
                  {formErrors.validity_period && (
                    <span className="admin-certifi-error-text">{formErrors.validity_period}</span>
                  )}
                  <small className="admin-certifi-field-hint">
                    Common: 30 days (1 month), 365 days (1 year), 730 days (2 years)
                  </small>
                </div>
              )}
            </div>

            <div className="admin-certifi-form-field">
              <label>Certificate Template (Optional)</label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={handleTemplateFileChange}
                disabled={uploadingTemplate}
                style={{ display: 'block' }}
              />
              {uploadingTemplate && (
                <small className="admin-certifi-field-hint">Uploading…</small>
              )}
              {formData.template_file_path && !uploadingTemplate && (
                <div
                  style={{
                    marginTop: 8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 10px',
                    background: '#f1f5f9',
                    borderRadius: 6,
                    fontSize: 13,
                  }}
                >
                  <Check size={14} color="#15803d" />
                  <span style={{ flex: 1 }}>{templateFileName || formData.template_file_path}</span>
                  <button
                    type="button"
                    onClick={handleClearTemplate}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}
                    aria-label="Remove template"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
              {templateError && (
                <span className="admin-certifi-error-text">{templateError}</span>
              )}
              <small className="admin-certifi-field-hint">
                Accepts PDF / PNG / JPG. The file is uploaded immediately and linked to this certification on save.
              </small>
            </div>
          </div>

          {/* Required Learning Items */}
          <div className="admin-certifi-form-section">
            <div className="admin-certifi-section-header">
              <BookOpen size={20} />
              <h3>Required Courses</h3>
            </div>

            <div className="admin-certifi-form-field">
              <label>
                Select Courses <span className="admin-certifi-required">*</span>
              </label>

              {loading && activeCourses.length === 0 ? (
                <div className="admin-certifi-loading-courses">
                  <div className="spinner"></div>
                  <p>Loading courses...</p>
                </div>
              ) : activeCourses.length === 0 ? (
                <div className="admin-certifi-empty-courses">
                  <p>No active courses available</p>
                </div>
              ) : (
                <>
                  <div className="admin-certifi-checkbox-list">
                    {activeCourses.map((course) => (
                      <label key={course.id} className="admin-certifi-checkbox-item">
                        <input
                          type="checkbox"
                          checked={formData.course_ids.includes(course.id)}
                          onChange={() => handleCourseToggle(course.id)}
                        />
                        <div className="admin-certifi-course-info">
                          <span className="admin-certifi-course-title">{course.title}</span>
                          <small className="admin-certifi-course-meta">
                            {course.level && <span className="badge">{course.level}</span>}
                            {course.course_duration && <span>{course.course_duration}</span>}
                            {course.category_name && <span>{course.category_name}</span>}
                          </small>
                        </div>
                      </label>
                    ))}
                  </div>

                  {formErrors.course_ids && (
                    <span className="admin-certifi-error-text">{formErrors.course_ids}</span>
                  )}

                  {/* Selected Courses Summary */}
                  {selectedCourseDetails.length > 0 && (
                    <div className="admin-certifi-selected-courses">
                      <h4>Selected Courses ({selectedCourseDetails.length})</h4>
                      <div className="admin-certifi-selected-courses-grid">
                        {selectedCourseDetails.map((course) => (
                          <div key={course.id} className="admin-certifi-selected-course-card">
                            <div className="admin-certifi-selected-course-header">
                              <span className="admin-certifi-selected-course-title">
                                {course.title}
                              </span>
                              <button
                                type="button"
                                className="admin-certifi-remove-course-btn"
                                onClick={() => handleCourseToggle(course.id)}
                                title="Remove course"
                              >
                                <X size={14} />
                              </button>
                            </div>
                            {course.short_description && (
                              <p className="admin-certifi-selected-course-desc">
                                {course.short_description}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="admin-certifi-create-sidebar">
          <div className="admin-certifi-sidebar-section">
            <h4>Actions</h4>
            <button
              className="admin-certifi-action-button admin-certifi-action-button-primary"
              onClick={() => handleSubmit('active')}
              disabled={submitting || loading}
            >
              {submitting ? (
                <>
                  <div className="spinner-small"></div>
                  <span>Publishing...</span>
                </>
              ) : (
                <>
                  <Check size={16} />
                  <span>Publish</span>
                </>
              )}
            </button>
            <button
              className="admin-certifi-action-button admin-certifi-action-button-secondary"
              onClick={() => handleSubmit('inactive')}
              disabled={submitting || loading}
            >
              <span>📋</span>
              <span>Save as Draft</span>
            </button>
            <button
              className="admin-certifi-action-button admin-certifi-action-button-secondary"
              onClick={() => {
                setFormData({
                  certification_name: '',
                  description: '',
                  validity_type: 'lifetime',
                  validity_period: undefined,
                  template_file_path: '',
                  status: 'active',
                  course_ids: [],
                });
                setFormErrors({});
                clearError();
                setSuccessMessage('');
                setTemplateFileName('');
                setTemplateError('');
              }}
              disabled={submitting}
            >
              <X size={16} />
              <span>Clear Form</span>
            </button>
          </div>

          <div className="admin-certifi-sidebar-section admin-certifi-tips-section">
            <h4>
              <span className="admin-certifi-tips-icon">💡</span>
              Certification Tips
            </h4>
            <ul className="admin-certifi-tips-list">
              <li>Use clear, descriptive certification names</li>
              <li>Set realistic validity periods for limited certifications</li>
              <li>Select all required courses for completion</li>
              <li>Save as draft to review before publishing</li>
            </ul>
          </div>

          {/* Summary */}
          <div className="admin-certifi-sidebar-section admin-certifi-summary-section">
            <h4>Summary</h4>
            <div className="admin-certifi-summary-item">
              <span className="admin-certifi-summary-label">Status:</span>
              <span className="admin-certifi-summary-value">
                {formData.status === 'active' ? 'Active' : 'Draft'}
              </span>
            </div>
            <div className="admin-certifi-summary-item">
              <span className="admin-certifi-summary-label">Validity:</span>
              <span className="admin-certifi-summary-value">
                {formData.validity_type === 'lifetime'
                  ? 'Lifetime'
                  : `${formData.validity_period || 0} days`}
              </span>
            </div>
            <div className="admin-certifi-summary-item">
              <span className="admin-certifi-summary-label">Courses:</span>
              <span className="admin-certifi-summary-value">{formData.course_ids.length}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCertification;
